import React from 'react';

import { Group, NumberInput, Stack, Text } from '@mantine/core';

import { blockNonDecimal, blockNonInteger, WeightResult, WeightSetEntry, WeightUnit } from './types';

interface WeightSetsInputProps {
  value: WeightResult;
  onChange: (value: WeightResult) => void;
  unit: WeightUnit;
  targetReps: ReadonlyArray<number>;
  enlarged?: boolean;
}

const WeightSetsInput = ({
  value,
  onChange,
  unit,
  targetReps,
  enlarged = false,
}: WeightSetsInputProps): JSX.Element => {
  const updateSet = (index: number, patch: Partial<WeightSetEntry>): void => {
    onChange({ ...value, sets: value.sets.map((s, i) => (i === index ? { ...s, ...patch } : s)) });
  };

  const uniformTarget =
    targetReps.length > 0 && targetReps.every(t => t === targetReps[0]) ? targetReps[0] : undefined;
  const titleSuffix = uniformTarget !== undefined ? ` - target ${uniformTarget} reps` : '';

  return (
    <Stack spacing='xs'>
      <Text size={enlarged ? 'md' : 'xs'} weight={600} color='dimmed' transform='uppercase'>
        Enter result for each set ({value.sets.length} sets{titleSuffix})
      </Text>

      {value.sets.length === 0 && (
        <Text size='sm' color='dimmed'>
          No sets configured for this movement.
        </Text>
      )}

      {value.sets.map((set, index) => (
        <Group key={index} spacing='xs' noWrap align='flex-end'>
          <Text size={enlarged ? 'lg' : 'sm'} color='dimmed' pb='xs' sx={{ minWidth: 48 }}>
            Set {index + 1}
          </Text>
          <Stack spacing={2} sx={{ flex: 1 }}>
            <Text size={enlarged ? 'md' : 'xs'} color='dimmed' transform='uppercase' weight={500}>
              Reps
            </Text>
            <NumberInput
              value={set.reps}
              onChange={reps => updateSet(index, { reps })}
              min={1}
              precision={0}
              inputMode='numeric'
              onKeyDown={blockNonInteger}
              placeholder={targetReps[index] !== undefined ? `target ${targetReps[index]}` : undefined}
              size={enlarged ? 'xl' : 'sm'}
            />
          </Stack>
          <Text size={enlarged ? 'lg' : 'sm'} color='dimmed' pb='xs'>
            @
          </Text>
          <Stack spacing={2} sx={{ flex: 1 }}>
            <Text size={enlarged ? 'md' : 'xs'} color='dimmed' transform='uppercase' weight={500}>
              Weight
            </Text>
            <NumberInput
              value={set.weight}
              onChange={weight => updateSet(index, { weight })}
              min={0}
              precision={1}
              step={0.5}
              inputMode='decimal'
              onKeyDown={blockNonDecimal}
              rightSection={
                <Text size={enlarged ? 'md' : 'xs'} color='dimmed' pr={4}>
                  {unit}
                </Text>
              }
              rightSectionWidth={36}
              size={enlarged ? 'xl' : 'sm'}
            />
          </Stack>
        </Group>
      ))}
    </Stack>
  );
};

export default WeightSetsInput;
